import { useState } from 'react';
import type { Tip, Review } from '../types';
import PageMeta from '../components/PageMeta';

const TIPS: Tip[] = [
  {
    icon: 'pi-wifi',
    category: 'Netzwerk',
    title: 'WLAN zu langsam? Diese 3 Dinge helfen sofort',
    excerpt: 'Der Router-Standort ist oft das größte Problem. Erfahren Sie, warum Wände, Mikrowellen und schlechte Kanalwahl Ihr WLAN ausbremsen – und wie Sie es ohne neue Hardware verbessern.',
    readTime: '3 Min.',
    color: '#38bdf8',
  },
  {
    icon: 'pi-home',
    category: 'SmartHome',
    title: 'Zigbee vs. Z-Wave vs. WLAN – was passt zu mir?',
    excerpt: 'Ein Überblick über die wichtigsten SmartHome-Funkprotokolle: Wo liegen die Unterschiede, was ist zukunftssicher, und wann lohnt sich welche Lösung?',
    readTime: '5 Min.',
    color: '#38bdf8',
  },
  {
    icon: 'pi-shield',
    category: 'Sicherheit',
    title: 'Router-Sicherheit: 5 Einstellungen, die Sie jetzt prüfen sollten',
    excerpt: 'Die meisten Router laufen mit Werkseinstellungen – das ist ein Risiko. Diese einfachen Anpassungen schützen Ihr Heimnetz ohne technisches Vorwissen.',
    readTime: '4 Min.',
    color: '#38bdf8',
  },
  {
    icon: 'pi-desktop',
    category: 'Windows',
    title: 'Windows läuft langsam – Ursachen und einfache Abhilfe',
    excerpt: 'Oft sind es kleine Einstellungen oder unnötige Autostart-Programme, die den PC bremsen. Hier erkläre ich Schritt für Schritt, wo man ansetzen sollte.',
    readTime: '4 Min.',
    color: '#38bdf8',
  },
];

const REVIEWS: Review[] = [
  {
    icon: 'pi-wifi',
    category: 'Router / Mesh',
    name: 'TP-Link Deco XE75 Pro',
    verdict: 'Empfehlenswert',
    verdictColor: '#34d399',
    stars: 4,
    summary: 'Sehr gutes Preis-Leistungs-Verhältnis für mittelgroße Wohnungen. Einfache Einrichtung, stabile Verbindung, etwas eingeschränkte Konfigurationsoptionen.',
    tags: ['WLAN 6E', 'Mesh', 'Einfache Einrichtung'],
  },
  {
    icon: 'pi-home',
    category: 'SmartHome Hub',
    name: 'Home Assistant Yellow',
    verdict: 'Für Technik-Affine',
    verdictColor: '#fde047',
    stars: 5,
    summary: 'Die flexibelste SmartHome-Zentrale am Markt. Volle Kontrolle, lokale Verarbeitung, riesige Community. Erfordert etwas Einarbeitungszeit.',
    tags: ['Zigbee', 'Z-Wave', 'Lokal', 'Open Source'],
  },
  {
    icon: 'pi-print',
    category: '3D Drucker',
    name: 'Bambu Lab A1 Mini',
    verdict: 'Top-Empfehlung',
    verdictColor: '#34d399',
    stars: 5,
    summary: 'Für Einsteiger und Fortgeschrittene gleichermaßen ideal. Druckt schnell, zuverlässig und mit minimalem Einrichten. Perfekter Einstieg in die 3D-Welt.',
    tags: ['Einsteiger', 'Schnell', 'Plug & Play'],
  },
];

const StarRating = ({ stars }: { stars: number }) => (
  <div className="flex gap-[3px]">
    {[1, 2, 3, 4, 5].map((i) => (
      <i key={i} className={`pi pi-star${i <= stars ? '-fill' : ''} text-[13px] ${i <= stars ? 'text-ak-yellow' : 'text-ak-dim'}`}></i>
    ))}
  </div>
);

const TipCard = ({ tip }: { tip: Tip }) => {
  return (
    <div className="group relative flex flex-col gap-3 overflow-hidden rounded-[18px] border border-white/5 bg-ak-dark px-[22px] pt-[22px] pb-[18px] shadow-[inset_0_2px_8px_rgba(0,0,0,0.2)] transition-[transform,background-color,border-color,box-shadow] duration-[250ms] hover:-translate-y-[3px] hover:border-ak-sky/30 hover:bg-ak-darker hover:shadow-[0_12px_32px_rgba(0,0,0,0.3)]">
      <div className="absolute inset-x-0 top-0 h-0.5 bg-ak-sky opacity-0 transition-opacity duration-[250ms] group-hover:opacity-100" />
      <div className="flex items-center justify-between">
        <span className="rounded-full bg-ak-sky/[0.09] px-2.5 py-[3px] text-[11px] font-semibold text-ak-sky">
          <i className={`pi ${tip.icon} mr-[5px] text-[10px]`}></i>
          {tip.category}
        </span>
        <span className="text-[11px] text-ak-dim">
          <i className="pi pi-clock mr-1 text-[10px]"></i>
          {tip.readTime}
        </span>
      </div>
      <h3 className="m-0 text-[15px] font-bold leading-[1.4] text-white">{tip.title}</h3>
      <p className="m-0 flex-1 text-[13px] leading-[1.6] text-ak-muted">{tip.excerpt}</p>
      <div className="flex items-center gap-[5px] text-[13px] font-medium text-ak-sky">
        Artikel lesen
        <i className="pi pi-arrow-right text-[11px] transition-transform group-hover:translate-x-[3px]"></i>
      </div>
    </div>
  );
};

const ReviewCard = ({ review }: { review: Review }) => {
  return (
    <div className="flex flex-col gap-3.5 rounded-[18px] border border-white/5 bg-ak-dark p-[22px] shadow-[inset_0_2px_8px_rgba(0,0,0,0.2)] transition-[transform,background-color,border-color,box-shadow] duration-[250ms] hover:-translate-y-[3px] hover:border-ak-sky/25 hover:bg-ak-darker hover:shadow-[0_12px_32px_rgba(0,0,0,0.3)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="mb-1 block text-[11px] text-ak-dim">{review.category}</span>
          <h3 className="m-0 text-base font-bold text-white">{review.name}</h3>
        </div>
        <span
          className="shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold"
          style={{ background: review.verdictColor + '20', color: review.verdictColor }}
        >
          {review.verdict}
        </span>
      </div>
      <StarRating stars={review.stars} />
      <p className="m-0 text-[13px] leading-[1.6] text-ak-muted">{review.summary}</p>
      <div className="flex flex-wrap gap-1.5">
        {review.tags.map((t, i) => (
          <span key={i} className="rounded-full bg-white/[0.06] px-2.5 py-[3px] text-[11px] text-ak-muted">{t}</span>
        ))}
      </div>
    </div>
  );
};

interface Props {
  onContactClick: () => void;
}

const KnowledgeBasePage = ({ onContactClick }: Props) => {
  const [activeTab, setActiveTab] = useState<'tipps' | 'reviews'>('tipps');

  return (
    <>
    {/* noIndex solange nur Vorschau-Beispiele ohne echte Artikel vorhanden sind */}
    <PageMeta
      title="Tipps & Geräte-Empfehlungen"
      description="Praxisnahe Anleitungen zur Selbsthilfe und ehrliche Bewertungen von Geräten – damit Sie informierte Entscheidungen treffen können."
      path="/wissen"
      noIndex
    />
    <section className="mx-auto max-w-[1152px] px-8 py-[clamp(40px,7vw,72px)]">
      <div className="mb-10">
        <h1 className="mb-3 text-[clamp(1.75rem,3vw,2.5rem)] font-bold text-white">
          Tipps & Geräte-Empfehlungen
        </h1>
        <p className="m-0 max-w-[520px] text-[15px] text-ak-muted">
          Praxisnahe Anleitungen zur Selbsthilfe und ehrliche Bewertungen von Geräten –
          damit Sie informierte Entscheidungen treffen können.
        </p>
      </div>

      {/* In-Entwicklung-Banner */}
      <div className="mb-9 flex flex-wrap items-center gap-3 rounded-[14px] border border-dashed border-ak-yellow/30 bg-ak-yellow/[0.06] px-5 py-3">
        <i className="pi pi-info-circle text-base text-ak-yellow"></i>
        <p className="m-0 text-[13px] text-ak-yellow">
          <strong>In Entwicklung:</strong> Die Knowledge Base wird laufend mit neuen Inhalten befüllt.
          Die Inhalte unten sind Vorschau-Beispiele für das geplante Format.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-8 flex gap-2 border-b border-white/[0.07]">
        {[
          { id: 'tipps' as const, label: 'Tipps & Anleitungen', icon: 'pi-lightbulb' },
          { id: 'reviews' as const, label: 'Geräte-Reviews', icon: 'pi-star' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`-mb-px flex cursor-pointer items-center gap-[7px] border-0 border-b-2 bg-transparent px-4 py-2.5 font-sans text-sm font-semibold transition-colors ${
              activeTab === tab.id
                ? 'border-ak-sky text-ak-sky'
                : 'border-transparent text-ak-dim hover:text-ak-muted'
            }`}
          >
            <i className={`pi ${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'tipps' && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {TIPS.map((tip, i) => <TipCard key={i} tip={tip} />)}
        </div>
      )}

      {activeTab === 'reviews' && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {REVIEWS.map((r, i) => <ReviewCard key={i} review={r} />)}
        </div>
      )}

      {/* Contribute CTA */}
      <div className="mt-12 rounded-[20px] border border-ak-sky/[0.12] bg-ak-dark/40 px-6 py-7 text-center">
        <i className="pi pi-comments mb-3 block text-[28px] text-ak-sky"></i>
        <p className="mb-1.5 text-sm text-ak-muted">
          <strong className="text-white">Gibt es ein Gerät, das Sie bewertet haben möchten?</strong>
        </p>
        <p className="mb-4 text-[13px] text-ak-dim">
          Schreiben Sie mir – ich nehme Wünsche für neue Inhalte gerne entgegen.
        </p>
        <button onClick={onContactClick} className="btn-secondary text-sm">
          <i className="pi pi-envelope"></i> Kontakt aufnehmen
        </button>
      </div>
    </section>
    </>
  );
};

export default KnowledgeBasePage;
